import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { KeyRound, Eye, EyeOff, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [validName, setValidName] = useState("");
  const [validError, setValidError] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidError("Missing reset token. Please use the link from your email.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-token/${encodeURIComponent(token)}`);
        setValidName(data?.name || "");
      } catch (err) {
        setValidError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-md">
        <div className="mb-8"><Logo size="md" /></div>

        {loading ? (
          <div data-testid="reset-loading" className="p-8 bg-white rounded-3xl card-tactile text-center text-stone">Checking your link…</div>
        ) : validError ? (
          <div data-testid="reset-invalid" className="p-8 bg-white rounded-3xl card-tactile text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-watermelon/15 text-watermelon flex items-center justify-center">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-ink mb-2">Link problem</h1>
            <p className="text-stone mb-6">{validError}</p>
            <div className="flex flex-col gap-2">
              <Link to="/forgot-password" className="inline-flex items-center justify-center px-5 py-2 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover">
                Request a new link
              </Link>
              <Link to="/login" className="text-sky-hover font-bold hover:underline text-sm">Back to sign in</Link>
            </div>
          </div>
        ) : done ? (
          <div data-testid="reset-done" className="p-8 bg-white rounded-3xl card-tactile text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-leaf/15 text-leaf flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-ink mb-2">Password updated</h1>
            <p className="text-stone">Redirecting to sign in…</p>
          </div>
        ) : (
          <div className="p-8 bg-white rounded-3xl card-tactile">
            <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-2 text-sky-hover font-bold text-sm mb-6 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-2xl bg-sky text-white flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5" />
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink leading-tight">Set a new password</h1>
            </div>
            {validName && <p className="text-stone mb-6">for <strong className="text-ink">{validName}</strong></p>}
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="block text-sm font-bold text-ink mb-1">New password</label>
                <input
                  data-testid="reset-new"
                  type={show ? "text" : "password"}
                  required
                  minLength={6}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-semibold"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-ink mb-1">Confirm new password</label>
                <input
                  data-testid="reset-confirm"
                  type={show ? "text" : "password"}
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-semibold"
                />
              </div>
              <button type="button" onClick={() => setShow(s => !s)} className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-hover hover:underline">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {show ? "Hide passwords" : "Show passwords"}
              </button>
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={submitting}
                className="w-full h-12 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
              >
                {submitting ? "Updating…" : "Update password"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
